import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { IReturnReason, IReturnReasonResponse } from "@/interfaces/ReturnReason/returnReason.interface";
import { AddReturnReason } from "./AddReturnReason";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";

import { IFilter, IPageInfo } from '@/interfaces/common.interface';
import { useFilter } from '@/hooks/useFilter';
import Pagination from '@/components/common/Pagination';
import { TableFilters } from '@/components/common/TableFilters';
import { CommonSelect } from '@/components/common/CommonSelect';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Switch } from '@/components/ui/switch';
// Assuming we don't have a specific resource yet, we use Settings or just not check it if not needed.
// For now, let's omit the check if it's admin-only module, or use RESOURCES.RETURNS if it exists.
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';

export const ReturnReasons = () => {
    const [reasons, setReasons] = useState<IReturnReason[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [selectedReason, setSelectedReason] = useState<IReturnReason | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);

    const { canView, canCreate, canEdit, canDelete } = usePermissions();

    // Deletion State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [reasonToDelete, setReasonToDelete] = useState<IReturnReason | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: "",
        status: "",
        page: 1,
        limit: 10
    });

    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0
    });

    const fetchReasons = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);

            const response = await callApi<IReturnReasonResponse>(
                `${API_URL + API_ENDPOINT.returnReason}/list?${queryParams.toString()}`,
                { method: 'GET' }
            );
            setReasons(response?.data?.returnReasons || []);
            setPageMetadata(
                response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 }
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReason = async () => {
        if (!reasonToDelete) return;
        setIsDeleting(true);
        try {
            await callApi(`${API_URL + API_ENDPOINT.returnReason}/${reasonToDelete.id}`, {
                method: 'DELETE'
            });
            showToastNotification('success', 'Return reason deleted successfully');
            setShowDeleteModal(false);
            fetchReasons();
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsDeleting(false);
            setReasonToDelete(null);
        }
    };

    // const handleToggleStatus = async (item: IReturnReason) => {
    //     setTogglingId(item.id);
    //     const newStatus = item.status === "active" ? "inactive" : "active";
    //     try {
    //         await callApi(`${API_URL + API_ENDPOINT.returnReason}/${item.id}`, {
    //             method: "PATCH",
    //             data: {
    //                 status: newStatus
    //             }
    //         });
    //         showToastNotification("success", "Return reason status updated successfully");
    //         setReasons(reasons.map(reason => reason.id === item.id ? { ...reason, status: newStatus } : reason));
    //     } catch (error) {
    //         ThrowError(error);
    //     } finally {
    //         setTogglingId(null);
    //     }
    // };

    useEffect(() => {
        fetchReasons();
    }, [debouncedFilters.search, debouncedFilters.status, debouncedFilters.page, debouncedFilters.limit]);

    const columns: TableColumn<IReturnReason>[] = [
        {
            key: 'reason',
            header: 'Return Reason',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">{item.reason}</span>
                </div>
            )
        },
        // {
        //     key: 'status',
        //     header: 'Status',
        //     render: (item) => (
        //         <div className="flex items-center gap-3">
        //             <Switch
        //                 checked={item.status === "active"}
        //                 onCheckedChange={() => handleToggleStatus(item)}
        //                 disabled={togglingId === item.id || !canEdit(RESOURCES.RETURNS)}
        //                 className="data-[state=checked]:bg-green-500"
        //             />
        //             <StatusBadge status={item.status || 'active'} />
        //         </div>
        //     )
        // },
        {
            key: 'createdAt',
            header: 'Created Date',
            render: (item) => (
                <span className="text-xs text-muted-foreground">
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '-'}
                </span>
            )
        }
    ];

    return (
        <Layout>
            <MainHeading
                title="Return Reasons"
                description="Manage the list of valid reasons for return requests"
                actionLabel={canCreate(RESOURCES.RETURNS) ? "Add Reason" : undefined}
                actionIcon={canCreate(RESOURCES.RETURNS) ? <Plus size={16} /> : undefined}
                onAction={() => {
                    setSelectedReason(null);
                    setModalMode('create');
                    setShowAddModal(true);
                }}
            />

            <div className="mt-6 space-y-2">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search reasons..."
                >
                    {/* <CommonSelect
                        value={filters.status || ""}
                        onValueChange={(val) => updateFilter({ status: val })}
                        options={[
                            { label: "Active", value: "active" },
                            { label: "Inactive", value: "inactive" }
                        ]}
                        triggerClassName="w-[180px]"
                        placeholder="Select Status"
                    /> */}
                </TableFilters>

                <CommonTable
                    columns={columns}
                    data={reasons}
                    loading={loading}
                    actions={(reason) => {
                        const availableActions: Action<IReturnReason>[] = [];
                        if (canEdit(RESOURCES.RETURNS)) {
                            availableActions.push({
                                text: 'Edit',
                                icon: <Pencil size={16} className="cursor-pointer" />,
                                onClick: (r) => {
                                    setSelectedReason(r);
                                    setModalMode('edit');
                                    setShowAddModal(true);
                                }
                            });
                        }
                        if (canDelete(RESOURCES.RETURNS)) {
                            availableActions.push({
                                text: 'Delete',
                                icon: <Trash2 size={16} className="text-destructive cursor-pointer" />,
                                onClick: (r) => {
                                    setReasonToDelete(r);
                                    setShowDeleteModal(true);
                                }
                            });
                        }
                        return availableActions;
                    }}
                />

                <Pagination
                    currentPage={pageMetadata.page}
                    totalPages={pageMetadata.totalPages}
                    totalItems={pageMetadata.total}
                    itemsPerPage={pageMetadata.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <AddReturnReason
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal}
                modalMode={modalMode}
                selectedReason={selectedReason}
                onSuccess={fetchReasons}
            />

            <ConfirmationModal
                open={showDeleteModal}
                onOpenChange={setShowDeleteModal}
                title="Delete Return Reason"
                message={`Are you sure you want to delete the reason "${reasonToDelete?.reason}"? This action cannot be undone.`}
                confirmText="Delete"
                onConfirm={handleDeleteReason}
                loading={isDeleting}
                size='lg'
            />
        </Layout>
    );
};
